import { useLayoutEffect, useRef, type ReactNode } from 'react';

/** Below this a word stops reading as part of the artwork. */
const MIN_FIT = 0.55;

type FitTag = 'span' | 'p' | 'h1' | 'h2' | 'h3';

/**
 * A label set smaller until it fits the room its slot gives it.
 *
 * The slots are painted, so their width is the picture's. Counting characters
 * against the English (`at()`) is right within one alphabet and wrong across
 * them: Russian capitals run wider than Latin ones, so a Russian word no longer
 * than its English one still spilled past the frame. This measures instead.
 * The stylesheet gives the element its room (a width, an inset or a
 * max-width), with the text on one line or on lines broken by hand; if the
 * text is wider, `--fit` takes the font down by exactly the overflow, never
 * below MIN_FIT, and the stylesheet multiplies its size by it.
 *
 * The text is measured as laid out (a Range around it), not by scrollWidth:
 * a centred label that overflows does so on both sides, and scrollWidth only
 * counts the side it can scroll to.
 *
 * `watch` names the text when the children are not a plain string, so the
 * measure is taken again when it changes. It is also taken again once the web
 * fonts have arrived, because the width depends on them, and whenever the
 * room itself changes width: a screen mounted while hidden measures a room of
 * zero, and without this its labels would never shrink once it is shown.
 */
export function FitText({
  as: Tag = 'span',
  className,
  children,
  watch,
  id,
  ariaHidden,
  min = MIN_FIT,
}: {
  as?: FitTag;
  className: string;
  children: ReactNode;
  watch?: string;
  id?: string;
  ariaHidden?: boolean;
  /** The smallest --fit allowed; lower only for a slot too small to hold a long word otherwise. */
  min?: number;
}) {
  const ref = useRef<HTMLElement>(null);
  const text = typeof children === 'string' || typeof children === 'number' ? String(children) : (watch ?? '');

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    let alive = true;
    let measuredRoom = -1;
    const fit = () => {
      if (!alive) return;
      measuredRoom = element.clientWidth;
      element.style.setProperty('--fit', '1');
      const range = document.createRange();
      if (typeof range.getBoundingClientRect !== 'function') return;
      range.selectNodeContents(element);
      const need = range.getBoundingClientRect().width;
      const style = window.getComputedStyle(element);
      const room = element.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (room > 0 && need > room + 0.5) element.style.setProperty('--fit', String(Math.max(min, room / need)));
    };
    fit();
    void document.fonts?.ready.then(fit);
    // Only a change in the room's width matters; the font shrinking inside a
    // fixed room does not move it, so this does not chase its own tail.
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => {
            if (element.clientWidth !== measuredRoom) fit();
          })
        : null;
    observer?.observe(element);
    return () => {
      alive = false;
      observer?.disconnect();
    };
  }, [text, min]);

  return (
    <Tag ref={ref as never} id={id} className={className} aria-hidden={ariaHidden || undefined}>
      {children}
    </Tag>
  );
}
